import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listEmitentes, listEmitentesAll } from '../api/emitente';
import { formatCNPJ } from '../utils/formatters';
import {
  Plus,
  Building2,
  Search,
  CheckCircle2,
  ArrowRightCircle,
  Pencil
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';

export default function EmitentesListPage() {
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const [incluirInativos, setIncluirInativos] = useState(false);
  const { emitenteId, setEmitenteId } = useEmitenteAtivo();

  const queryFn = incluirInativos ? listEmitentesAll : listEmitentes;
  const { data, isLoading } = useQuery({
    queryKey: ['emitentes', { incluirInativos }],
    queryFn
  });

  const filtrados = useMemo(() => {
    const lista = data ?? [];
    // Busca case-insensitive em ambos os lados. CNPJ alfanumerico (PC3D315K000193)
    // tambem casa porque tanto q quanto cnpj sao normalizados para upper.
    const q = busca.trim().toUpperCase();
    if (!q) return lista;
    return lista.filter((e) => {
      const nome = (e.nome_fantasia || e.razao_social || '').toUpperCase();
      const cnpj = (e.cnpj || '').toUpperCase();
      return nome.includes(q) || cnpj.includes(q);
    });
  }, [data, busca]);

  function acessarEmpresa(id: number) {
    setEmitenteId(id);
    navigate(`/empresa/${id}`);
  }

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="eyebrow">Emitentes</div>
          <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">Empresas cadastradas</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
            Clique em <b>Abrir</b> para acessar o hub da empresa com certificado, módulos e documentos.
          </p>
        </div>
        <Link to="/emitentes/novo" className="btn-primary min-h-11 justify-center">
          <Plus className="h-4 w-4" />
          Nova empresa
        </Link>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por razão social, nome fantasia ou CNPJ..."
            className="field-input pl-9"
          />
        </div>
        <label className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 select-none dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300">
          <input
            type="checkbox"
            checked={incluirInativos}
            onChange={(e) => setIncluirInativos(e.target.checked)}
            className="rounded border-slate-300 bg-white text-emerald-600 focus:ring-emerald-500/30 dark:border-slate-700 dark:bg-slate-950 dark:text-emerald-500"
          />
          Mostrar inativas
        </label>
      </div>
      </div>

      {isLoading && (
        <div className="py-10 grid place-items-center">
          <LoadingSpinner label="Carregando empresas..." />
        </div>
      )}

      {!isLoading && (data?.length ?? 0) === 0 && (
        <EmptyState
          title="Nenhuma empresa cadastrada"
          description="Cadastre a empresa emitente dos documentos."
          icon={<Building2 className="h-10 w-10" />}
          action={
            <Link to="/emitentes/novo" className="btn-primary">
              <Plus className="h-4 w-4" />
              Cadastrar empresa
            </Link>
          }
        />
      )}

      {!isLoading && (data?.length ?? 0) > 0 && filtrados.length === 0 && (
        <EmptyState
          title="Nenhum resultado"
          description={`Nenhuma empresa encontrada para "${busca}".`}
          icon={<Search className="h-10 w-10" />}
        />
      )}

      {!isLoading && filtrados.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtrados.map((e) => {
            const ativo = e.id === emitenteId;
            const isInativo = e.ativo === false;
            const nome = e.nome_fantasia || e.razao_social || '(sem razão social)';
            const ambiente = e.ambiente === 'producao' ? 'producao' : 'homologacao';
            const cidadeUf = [e.xmun, e.uf].filter(Boolean).join(' - ');
            return (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => acessarEmpresa(e.id)}
                  className={`w-full text-left card-surface p-4 flex flex-col gap-3 h-full transition-all ${
                    ativo
                      ? 'ring-2 ring-primary border-primary shadow-md'
                      : 'hover:shadow-md hover:border-primary/50'
                  } ${isInativo ? 'opacity-60' : ''}`}
                  disabled={isInativo}
                >
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 h-10 w-10 rounded-lg bg-primary/10 grid place-items-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-950 truncate dark:text-slate-100" title={nome}>{nome}</h3>
                        {ativo && (
                          <span className="badge-success inline-flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Ativa
                          </span>
                        )}
                        {isInativo && <span className="badge-neutral">Inativa</span>}
                      </div>
                      {e.nome_fantasia && e.razao_social && e.nome_fantasia !== e.razao_social && (
                      <div className="text-xs text-slate-600 truncate dark:text-slate-400">
                          {e.razao_social}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-slate-600 flex flex-wrap gap-x-3 dark:text-slate-400">
                        <span className="font-mono">{formatCNPJ(e.cnpj)}</span>
                        {cidadeUf && <span>{cidadeUf}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className={ambiente === 'producao' ? 'badge-danger' : 'badge-warning'}>
                      {ambiente === 'producao' ? 'Produção' : 'Homologação'}
                    </span>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-end">
                      <Link
                        to={`/empresa/${e.id}/editar`}
                        onClick={(ev) => ev.stopPropagation()}
                        className="btn-ghost justify-center !px-3 !py-1.5 text-sm"
                        title="Editar dados, certificado, ambiente"
                      >
                        <Pencil className="h-4 w-4" />
                        Editar
                      </Link>
                      <span className="btn-primary justify-center !px-3 !py-1.5 text-sm pointer-events-none">
                        <ArrowRightCircle className="h-4 w-4" />
                        Abrir
                      </span>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
